'use client'

import { useEffect, useRef, type ReactNode } from 'react'

import { useOptionalChromeAnalytics, useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'

import { FOOTER_CLICK_ATTR, FOOTER_CLICK_MODULE_NAME } from '../clickTracking'

const OUTBOUND = 'outbound'

function parseUrl(href: string): URL | null {
  try {
    return new URL(href, window.location.origin)
  } catch {
    return null
  }
}

/**
 * Emite el `click` del footer (`Footer.csv`) por delegación: un único listener
 * sobre `<footer>` que lee los `data-fc-*` del enlace pulsado. Un solo push por
 * click aunque el footer renderice acordeón móvil y grid de escritorio, y los
 * enlaces siguen siendo server components. Los elementos sin `data-fc-type`
 * (p. ej. `Accessibility`) se ignoran.
 *
 * El footer es hermano de `{children}`: usa el contexto del chrome y, sin
 * `ChromeAnalyticsProvider` (tests, `(canales)`), el de página; sin ninguno, no-op.
 * Los outbound abren en pestaña nueva (`target="_blank"`), así que el push
 * síncrono al dataLayer no compite con una descarga de página.
 */
export function FooterClickTracker({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLElement>(null)
  const chrome = useOptionalChromeAnalytics()
  const page = useOptionalPageAnalytics()
  const trackClick = chrome?.trackClick ?? page?.trackClick

  useEffect(() => {
    const footer = ref.current
    if (!footer || !trackClick) return

    function handleClick(event: MouseEvent) {
      const target = event.target
      if (!(target instanceof Element)) return
      const link = target.closest<HTMLAnchorElement>(`a[${FOOTER_CLICK_ATTR}]`)
      if (!link || !footer?.contains(link)) return

      const data = link.dataset
      const clickType = data.fcType ?? NO_APLICA
      const outbound = clickType === OUTBOUND
      const url = data.fcOmitLink ? null : parseUrl(link.getAttribute('href') ?? '')
      const text = data.fcText ?? link.textContent ?? ''

      trackClick?.({
        click_type: clickType,
        click_subtype: data.fcSubtype ?? NO_APLICA,
        link_text: text ? slugify(text) : NO_APLICA,
        link_url: !url ? NO_APLICA : outbound ? url.href : url.pathname,
        link_domain: url && outbound ? url.hostname.replace(/^www\./, '') : NO_APLICA,
        module_name: FOOTER_CLICK_MODULE_NAME,
        module_type: data.fcModuleType ?? NO_APLICA,
        nav_level: NO_APLICA,
        nav_path: NO_APLICA,
        action_detail: data.fcDetail ?? NO_APLICA,
        action_value: NO_APLICA,
        action_context: data.fcContext ?? NO_APLICA,
      })
    }

    footer.addEventListener('click', handleClick)
    return () => footer.removeEventListener('click', handleClick)
  }, [trackClick])

  return (
    <footer ref={ref} className={className}>
      {children}
    </footer>
  )
}
